import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { MilestoneFormData, MilestoneStatus } from "./milestonesTypes";

interface CreateMilestoneProps {
  open: boolean;
  saving: boolean;
  error: string | null;
  initialData: MilestoneFormData;
  onClose: () => void;
  onSave: (data: MilestoneFormData) => void;
}

const statusOptions: { value: MilestoneStatus; label: string }[] = [
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "complete", label: "Complete" },
];

export function CreateMilestone({
  open,
  saving,
  error,
  initialData,
  onClose,
  onSave,
}: CreateMilestoneProps) {
  const [formData, setFormData] = useState<MilestoneFormData>(initialData);

  useEffect(() => {
    if (open) {
      setFormData(initialData);
    }
  }, [initialData, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData.title ? "Edit milestone" : "Create milestone"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Milestone title"
            value={formData.title}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                title: event.target.value,
              }))
            }
            fullWidth
            required
          />
          <TextField
            label="Target date"
            type="date"
            value={formData.targetDate}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                targetDate: event.target.value,
              }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Percentage weight"
            type="number"
            value={formData.percentage}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                percentage: event.target.value,
              }))
            }
            fullWidth
            inputProps={{ min: 0, max: 100, step: 1 }}
            helperText="0 to 100"
          />
          <TextField
            select
            label="Status"
            value={formData.status}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                status: event.target.value as MilestoneStatus,
              }))
            }
            fullWidth
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(formData)}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
